import React from 'react';
import { Badge, mergeClasses, Image } from '@fluentui/react-components';
import { isMobile } from 'react-device-detect';

import EnhancedButton from 'src/components/atoms/enhanced-button/EnhancedButton';
import { SpotlightLayoutType, MediaType } from 'src/utils/enums';
import { Video } from 'src/models/Video';
import { openExternalUrl } from 'src/utils/utils';
import HeroMediaLayout from '../hero-media-layout/HeroMediaLayout';
import VideoPlayerDialog from '../video-player-dialog/VideoPlayerDialog';
import useSpotlightContentStyles from './SpotlightCardContent.styles';

export interface SpotlightContent {
  headline?: string
  bodyHeader?: string
  body: string
  callToAction: string
  actionURL: string
  badgeIcon?: JSX.Element
  mediaUrl?: string
  mediaType?: MediaType
  altText?: string
  nameRow1?: string
  nameRow2?: string
  nameRow3?: string
  videoId?: string
  videoPoster?: string
}
interface SpotlightCardContentProps {
  content: SpotlightContent
  layoutType: SpotlightLayoutType
}

const SpotlightCardContent: React.FC<SpotlightCardContentProps> = ({ content, layoutType }) => {
  const styles = useSpotlightContentStyles();

  const {
    nameRow1 = '',
    nameRow2 = '',
    nameRow3 = '',
    headline = '',
    bodyHeader = '',
    body = '',
    callToAction = '',
    actionURL = '',
    badgeIcon,
    mediaUrl = '',
    mediaType = MediaType.IMAGE,
    altText = '',
    videoId = '',
    videoPoster = '',
  } = content || {};

  const renderMedia = (): JSX.Element | null => {
    if (!mediaUrl) {
      return null;
    }

    const mediaContentClass = isMobile
      ? mergeClasses('media-content-mobile', styles.mediaContentMobile)
      : mergeClasses('media-desktop-content', styles.mediaContent);

    if (mediaType === MediaType.VIDEO) {
      const videoInfo: Video = {
        videoId: videoId,
        videoHeadline: headline,
        thumbnailSrc: videoPoster,
        mediaUrl,
      };
      return <VideoPlayerDialog videoInfo={videoInfo} mediaContentClassName={mediaContentClass} />;
    }

    return <Image src={mediaUrl} alt={altText} loading="lazy" role="presentation" className={mediaContentClass} />;
  };

  const renderBadgeOrHeadline = (): JSX.Element | null => {
    if (badgeIcon && headline) {
      return (
        <Badge
          appearance="filled"
          icon={badgeIcon}
          color="informative"
          size="extra-large"
          data-testid="daily-habit-spotlight-badge"
          className={styles.badge}
        >
          <span className={styles.badgeText}>{headline}</span>
        </Badge>
      );
    }

    if (!badgeIcon && headline) {
      return <div className={styles.title}>{headline}</div>;
    }

    return null;
  };

  const spotLightButton = (
    <EnhancedButton
      className={styles.buttonExternalLink}
      onClick={() => openExternalUrl(actionURL)}
      isExternal
    >
      {callToAction}
    </EnhancedButton>
  );

  const renderDescription = (): JSX.Element => (
    <div data-testid="spotlight-content" className={styles.contentContainer}>
      <section className={styles.description}>
        {renderBadgeOrHeadline()}
        {bodyHeader && <div className={styles.title}>{bodyHeader}</div>}
        <p>{body}</p>
      </section>

      {spotLightButton}
    </div>
  );

  const renderFullSizeDescription = (): JSX.Element => (
    <div className={styles.fullSizeContainer} data-testid="full-size-container">
      <div className={styles.fullSizeSubtitleContainer} data-testid="full-size-subtitle-container">
        <div className={styles.subtitle1}>{nameRow1}</div>

        <div className={styles.subtitle2}>{nameRow2}</div>

        <div className={styles.subtitle1}>{nameRow3}</div>
      </div>

      <section data-testid="full-size-body-container">
        <div className={styles.headerFullSize}>{headline}</div>

        <div className={styles.subtitle1}>{body}</div>
      </section>
    </div>
  );

  if (layoutType === SpotlightLayoutType.FULL_SIZE) {
    return (
      <HeroMediaLayout
        mediaContent={renderMedia()}
        content={renderFullSizeDescription()}
        type={layoutType}
        footer={spotLightButton}
      />
    );
  }

  return <HeroMediaLayout mediaContent={renderMedia()} content={renderDescription()} type={layoutType} />;
};

export default SpotlightCardContent;
